import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Button, Chip, Card, CardContent,
  CardActions, Divider, Alert, CircularProgress, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Grid
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Description as DocIcon,
  CheckCircle as VerifiedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  CheckCircleOutline as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  TaskAlt as CompleteIcon,
  Settings as SettingsIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_EXT_LABEL = 'PDF, JPG, PNG';

const DOC_TYPE_LABELS = {
  birth_certificate:    'Birth Certificate',
  transfer_certificate: 'Transfer Certificate',
  aadhaar:              'Aadhaar',
  photo:                'Photo',
  report_card:          'Report Card',
  other:                'Other',
};

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: '#e3f2fd', textColor: '#1565c0', Icon: PendingIcon },
  verified:  { label: 'Verified',  color: '#e8f5e9', textColor: '#2e7d32', Icon: VerifiedIcon },
  rejected:  { label: 'Rejected',  color: '#ffebee', textColor: '#c62828', Icon: RejectedIcon },
};

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function FileTypeIcon({ mime }) {
  if (mime === 'application/pdf') return <PdfIcon sx={{ color: '#e53935' }} />;
  if (mime?.startsWith('image/')) return <ImageIcon sx={{ color: '#1976d2' }} />;
  return <FileIcon />;
}

function calcAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const beforeBirthday = today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}

function StudentInfoHeader({ student }) {
  if (!student) return null;

  const classLabel = student.class_name
    ? `${student.class_name}${student.class_section ? ` - ${student.class_section}` : ''}`
    : 'Not assigned';
  const age = calcAge(student.date_of_birth);

  const details = [
    { label: 'Admission No.', value: student.admission_number || '—' },
    { label: 'Class', value: classLabel },
    { label: 'Gender', value: student.gender || '—' },
    { label: 'Age', value: age !== null ? `${age} yrs` : '—' },
    { label: 'Phone', value: student.phone || '—' },
    { label: "Father's Name", value: student.father_name || '—' },
    { label: "Mother's Name", value: student.mother_name || '—' },
  ];

  return (
    <Paper sx={{ p: 2.5, mb: 2, bgcolor: '#f5f9ff', border: '1px solid #d3e3f7' }} elevation={0}>
      <Typography variant="h6" fontWeight={700} color="#1a3c5e" mb={1.5}>
        {student.name}
      </Typography>
      <Grid container spacing={1.5}>
        {details.map(d => (
          <Grid item xs={6} sm={4} md={3} key={d.label}>
            <Typography variant="caption" color="text.secondary" display="block">
              {d.label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {d.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function CompliancePanel({ documents, requiredTypes }) {
  if (requiredTypes.length === 0) return null;

  const uploadedTypes = documents.map(d => d.document_type);
  const allComplete = requiredTypes.every(t => uploadedTypes.includes(t));

  return (
    <Paper
      sx={{
        p: 2.5, mb: 2,
        border: '2px solid',
        borderColor: allComplete ? '#43a047' : '#ef6c00',
        bgcolor: allComplete ? '#f1fdf3' : '#fff8f0',
      }}
      elevation={0}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        {allComplete
          ? <CompleteIcon sx={{ color: '#2e7d32' }} />
          : <PendingIcon sx={{ color: '#e65100' }} />
        }
        <Typography variant="subtitle2" fontWeight={700} color={allComplete ? '#2e7d32' : '#e65100'}>
          {allComplete ? 'All required documents uploaded' : 'Required documents checklist'}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {requiredTypes.map(type => {
          const uploaded = uploadedTypes.includes(type);
          return (
            <Chip
              key={type}
              icon={
                uploaded
                  ? <CheckIcon sx={{ fontSize: '0.9rem !important', color: '#2e7d32 !important' }} />
                  : <UncheckedIcon sx={{ fontSize: '0.9rem !important', color: '#c62828 !important' }} />
              }
              label={DOC_TYPE_LABELS[type] || type}
              size="small"
              sx={{
                bgcolor: uploaded ? '#e8f5e9' : '#ffebee',
                color:   uploaded ? '#2e7d32' : '#c62828',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function StudentDocuments() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [student, setStudent] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [meta, setMeta] = useState({ count: 0, limit: 5 });
  const [requiredTypes, setRequiredTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, doc: null, status: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [docType, setDocType] = useState('other');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, reqRes] = await Promise.all([
        principalAPI.documents.list(studentId),
        principalAPI.documents.getRequired(),
      ]);
      setStudent(docsRes.data.student || null);
      setDocuments(docsRes.data.documents || []);
      setMeta(docsRes.data.meta || { count: 0, limit: 5 });
      setRequiredTypes(reqRes.data.required_types || []);
    } catch {
      setAlert({ severity: 'error', message: 'Failed to load documents.' });
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleDownload(doc) {
    try {
      const { data } = await principalAPI.documents.download(studentId, doc.id);
      triggerDownload(data, doc.original_name);
    } catch {
      setAlert({ severity: 'error', message: 'Download failed.' });
    }
  }

  function validateFiles(files) {
    const errors = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        errors.push(`"${f.name}": only ${ALLOWED_EXT_LABEL} allowed.`);
      } else if (f.size > MAX_FILE_BYTES) {
        errors.push(`"${f.name}": exceeds 5 MB limit.`);
      }
    }
    return errors;
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';

    const available = meta.limit - meta.count;
    if (files.length > available) {
      setAlert({ severity: 'warning', message: `You can only add ${available} more document${available !== 1 ? 's' : ''}. Selected ${files.length}.` });
      return;
    }

    const errors = validateFiles(files);
    setFileErrors(errors);
    if (errors.length === 0) {
      setSelectedFiles(files);
      setAlert(null);
    }
  }

  async function handleUpload() {
    if (selectedFiles.length === 0) return;

    const errors = validateFiles(selectedFiles);
    if (errors.length > 0) {
      setFileErrors(errors);
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      for (const f of selectedFiles) {
        fd.append('documents', f);
      }
      const { data } = await principalAPI.documents.upload(studentId, fd);
      setDocuments(prev => [...(data.documents || []), ...prev]);
      setMeta(data.meta);
      setSelectedFiles([]);
      setDocType('other');
      setFileErrors([]);
      setAlert({ severity: 'success', message: `${data.documents.length} document${data.documents.length !== 1 ? 's' : ''} uploaded successfully.` });
    } catch (err) {
      const msg = err.response?.data?.message || 'Upload failed. Please try again.';
      setAlert({ severity: 'error', message: msg });
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (!deleteDialog.doc) return;
    setActionLoading(true);
    try {
      await principalAPI.documents.remove(studentId, deleteDialog.doc.id);
      setDocuments(prev => prev.filter(d => d.id !== deleteDialog.doc.id));
      setMeta(prev => ({ ...prev, count: prev.count - 1 }));
      setDeleteDialog({ open: false, doc: null });
      setAlert({ severity: 'success', message: 'Document deleted.' });
    } catch {
      setAlert({ severity: 'error', message: 'Delete failed.' });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleStatusUpdate() {
    if (!statusDialog.doc || !statusDialog.status) return;
    setActionLoading(true);
    try {
      const { data } = await principalAPI.documents.updateStatus(studentId, statusDialog.doc.id, statusDialog.status);
      setDocuments(prev => prev.map(d => d.id === data.document.id ? data.document : d));
      setStatusDialog({ open: false, doc: null, status: '' });
      setAlert({ severity: 'success', message: `Document marked as ${statusDialog.status}.` });
    } catch {
      setAlert({ severity: 'error', message: 'Status update failed.' });
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1} mt={1}>
          <IconButton onClick={() => navigate('/principal/students')} size="small">
            <BackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={700} color="#1a3c5e">
            Student Documents
          </Typography>
          <Chip
            label={`${meta.count} / ${meta.limit}`}
            size="small"
            sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
          />
          <Box flex={1} />
          <Tooltip title="Configure required documents">
            <IconButton size="small" onClick={() => navigate('/principal/required-documents')}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {alert && (
          <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <StudentInfoHeader student={student} />
            <CompliancePanel documents={documents} requiredTypes={requiredTypes} />

            {meta.count < meta.limit && (
              <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                <Typography variant="subtitle1" fontWeight={700} mb={2}>
                  Upload Document
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Document Type</InputLabel>
                      <Select
                        value={docType}
                        label="Document Type"
                        onChange={e => setDocType(e.target.value)}
                      >
                        {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                          <MenuItem key={v} value={v}>{l}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      fullWidth
                    >
                      Choose Files (up to {meta.limit - meta.count})
                    </Button>
                  </Grid>
                </Grid>

                {fileErrors.length > 0 && (
                  <Box mt={1.5}>
                    {fileErrors.map((e, i) => (
                      <Alert key={i} severity="error" sx={{ mb: 0.5, py: 0 }}>{e}</Alert>
                    ))}
                  </Box>
                )}

                {selectedFiles.length > 0 && fileErrors.length === 0 && (
                  <Box mt={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Selected: {selectedFiles.map(f => f.name).join(', ')}
                    </Typography>
                    <Box mt={1.5} display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                      >
                        {uploading ? 'Uploading…' : 'Upload'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {documents.length === 0 ? (
              <EmptyState
                icon={DocIcon}
                title="No documents uploaded for this student."
              />
            ) : (
              <Stack spacing={1.5}>
                {documents.map(doc => {
                  const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.submitted;
                  return (
                    <Card key={doc.id} elevation={2}>
                      <CardContent sx={{ pb: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <FileTypeIcon mime={doc.mime_type} />
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap title={doc.original_name}>
                              {doc.original_name}
                            </Typography>
                            <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" gap={0.5}>
                              <Chip
                                label={DOC_TYPE_LABELS[doc.document_type] || doc.document_type}
                                size="small"
                                sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontSize: '0.7rem' }}
                              />
                              <Chip
                                icon={<sc.Icon sx={{ fontSize: '0.85rem !important' }} />}
                                label={sc.label}
                                size="small"
                                sx={{ bgcolor: sc.color, color: sc.textColor, fontSize: '0.7rem' }}
                              />
                              <Typography variant="caption" color="text.secondary" alignSelf="center">
                                {formatBytes(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString('en-IN')}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ px: 2, py: 0.75, justifyContent: 'flex-end', gap: 0.5 }}>
                        <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload(doc)}>
                          Download
                        </Button>
                        <Button
                          size="small"
                          color="success"
                          onClick={() => setStatusDialog({ open: true, doc, status: doc.status === 'verified' ? 'rejected' : 'verified' })}
                        >
                          {doc.status === 'verified' ? 'Reject' : 'Verify'}
                        </Button>
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, doc })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </Container>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, doc: null })} fullWidth maxWidth="sm">
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete "<strong>{deleteDialog.doc?.original_name}</strong>"? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, doc: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, doc: null, status: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Update Document Status</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Update status for "<strong>{statusDialog.doc?.original_name}</strong>":
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusDialog.status}
              label="Status"
              onChange={e => setStatusDialog(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, doc: null, status: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusUpdate} disabled={actionLoading || !statusDialog.status}>
            {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
